import { Service } from '@toeverything/infra';

import type { CacheStorage } from '../../storage';

export const USER_AVATAR_CACHE_LIMIT = 256;
export const WORKSPACE_LOGO_CACHE_LIMIT = 64;
export const MAX_AVATAR_ASSET_BYTES = 8 * 1024 * 1024;

const CACHE_VERSION = 1;
const CACHE_PREFIX = 'streamlain:avatar-asset-cache:v1';

export type AvatarAssetCacheKind = 'user-avatar' | 'workspace-logo';

export type AvatarAssetCacheScope = {
  accountId: string;
  serverId: string;
};

export type AvatarAssetCacheInput = {
  kind: AvatarAssetCacheKind;
  scope: AvatarAssetCacheScope;
  entityId: string;
  source: string;
};

export type AvatarAssetCacheRecord = AvatarAssetCacheInput & {
  version: 1;
  mime: string;
  bytes: ArrayBuffer;
  cachedAt: number;
  lastAccessedAt: number;
};

type AvatarAssetBytes = {
  bytes: ArrayBuffer;
  mime: string;
};

function encodePart(part: string): string {
  return encodeURIComponent(part);
}

function scopePart(scope: AvatarAssetCacheScope): string {
  return `${encodePart(scope.serverId)}:${encodePart(scope.accountId)}`;
}

export function getAvatarAssetCacheBucketPrefix(
  kind: AvatarAssetCacheKind,
  scope: AvatarAssetCacheScope
): string {
  return [CACHE_PREFIX, kind, scopePart(scope)].join(':');
}

export function getAvatarAssetCacheEntityPrefix(
  input: Omit<AvatarAssetCacheInput, 'source'>
): string {
  return [
    getAvatarAssetCacheBucketPrefix(input.kind, input.scope),
    encodePart(input.entityId),
  ].join(':');
}

export function getAvatarAssetCacheKey(input: AvatarAssetCacheInput): string {
  return [
    getAvatarAssetCacheEntityPrefix(input),
    encodePart(input.source),
  ].join(':');
}

export function avatarAssetCacheLimit(kind: AvatarAssetCacheKind): number {
  return kind === 'user-avatar'
    ? USER_AVATAR_CACHE_LIMIT
    : WORKSPACE_LOGO_CACHE_LIMIT;
}

function isHttpUrl(url: string): boolean {
  return url.startsWith('http:') || url.startsWith('https:');
}

export function toAbsoluteAvatarAssetUrl(
  avatarUrl: string,
  baseUrl: string
): string | null {
  if (avatarUrl.startsWith('blob:') || avatarUrl.startsWith('data:image/')) {
    return null;
  }

  try {
    const parsed = new URL(avatarUrl, baseUrl);
    return isHttpUrl(parsed.href) ? parsed.href : null;
  } catch {
    return null;
  }
}

export function isAvatarAssetResponseCacheable(
  response: Pick<Response, 'headers' | 'ok'>,
  byteLength: number
): boolean {
  const mime = response.headers.get('content-type')?.split(';')[0]?.trim();
  return (
    response.ok &&
    !!mime?.startsWith('image/') &&
    byteLength > 0 &&
    byteLength <= MAX_AVATAR_ASSET_BYTES
  );
}

export function isAvatarAssetBlobCacheable(blob: Blob): boolean {
  const mime = blob.type.split(';')[0]?.trim();
  return (
    blob.size > 0 &&
    blob.size <= MAX_AVATAR_ASSET_BYTES &&
    (!mime || mime.startsWith('image/'))
  );
}

export function createAvatarAssetObjectUrl(
  record: AvatarAssetCacheRecord
): string {
  return URL.createObjectURL(new Blob([record.bytes], { type: record.mime }));
}

function isAvatarAssetCacheRecord(
  value: AvatarAssetCacheRecord | undefined,
  input: AvatarAssetCacheInput
): value is AvatarAssetCacheRecord {
  return (
    value?.version === CACHE_VERSION &&
    value.kind === input.kind &&
    value.scope.serverId === input.scope.serverId &&
    value.scope.accountId === input.scope.accountId &&
    value.entityId === input.entityId &&
    value.source === input.source &&
    typeof value.mime === 'string' &&
    value.mime.startsWith('image/') &&
    value.bytes instanceof ArrayBuffer &&
    value.bytes.byteLength > 0 &&
    value.bytes.byteLength <= MAX_AVATAR_ASSET_BYTES &&
    typeof value.cachedAt === 'number' &&
    typeof value.lastAccessedAt === 'number'
  );
}

async function fetchAvatarAssetBytes(
  sourceUrl: string,
  serverBaseUrl: string
): Promise<AvatarAssetBytes | null> {
  const avatarOrigin = new URL(sourceUrl).origin;
  const serverOrigin = new URL(serverBaseUrl).origin;
  const response = await fetch(sourceUrl, {
    cache: 'no-store',
    credentials: avatarOrigin === serverOrigin ? 'include' : 'omit',
    mode: 'cors',
  });
  const bytes = await response.arrayBuffer();

  if (!isAvatarAssetResponseCacheable(response, bytes.byteLength)) {
    return null;
  }

  return {
    bytes,
    mime:
      response.headers.get('content-type')?.split(';')[0]?.trim() ??
      'image/png',
  };
}

export class AvatarAssetCache {
  private readonly inFlightWrites = new Map<
    string,
    Promise<AvatarAssetCacheRecord | null>
  >();

  constructor(private readonly cacheStorage: CacheStorage) {}

  async get(
    input: AvatarAssetCacheInput
  ): Promise<AvatarAssetCacheRecord | null> {
    const key = getAvatarAssetCacheKey(input);
    const cached = await this.cacheStorage.get<AvatarAssetCacheRecord>(key);
    if (!isAvatarAssetCacheRecord(cached, input)) {
      if (cached) {
        await this.cacheStorage.del(key);
      }
      return null;
    }

    const touched = {
      ...cached,
      lastAccessedAt: Date.now(),
    };
    await this.cacheStorage.set(key, touched);
    return touched;
  }

  async cacheUrl(
    input: AvatarAssetCacheInput,
    serverBaseUrl: string
  ): Promise<AvatarAssetCacheRecord | null> {
    return this.withInFlight(input, async () => {
      const asset = await fetchAvatarAssetBytes(input.source, serverBaseUrl);
      return asset ? this.write(input, asset) : null;
    });
  }

  async objectUrl(
    input: AvatarAssetCacheInput,
    serverBaseUrl: string
  ): Promise<string | null> {
    const cached = await this.get(input);
    if (cached) {
      void this.cacheUrl(input, serverBaseUrl).catch(() => {});
      return createAvatarAssetObjectUrl(cached);
    }

    const refreshed = await this.cacheUrl(input, serverBaseUrl);
    return refreshed ? createAvatarAssetObjectUrl(refreshed) : null;
  }

  async cacheBlob(
    input: AvatarAssetCacheInput,
    blob: Blob
  ): Promise<AvatarAssetCacheRecord | null> {
    if (!isAvatarAssetBlobCacheable(blob)) {
      return null;
    }

    const bytes = await blob.arrayBuffer();
    return this.write(input, {
      bytes,
      mime: blob.type.split(';')[0]?.trim() || 'image/png',
    });
  }

  private async withInFlight(
    input: AvatarAssetCacheInput,
    loader: () => Promise<AvatarAssetCacheRecord | null>
  ) {
    const key = getAvatarAssetCacheKey(input);
    const existing = this.inFlightWrites.get(key);
    if (existing) {
      return existing;
    }

    const next = loader().finally(() => {
      this.inFlightWrites.delete(key);
    });
    this.inFlightWrites.set(key, next);
    return next;
  }

  private async write(
    input: AvatarAssetCacheInput,
    asset: AvatarAssetBytes
  ): Promise<AvatarAssetCacheRecord> {
    const now = Date.now();
    const record: AvatarAssetCacheRecord = {
      ...input,
      version: CACHE_VERSION,
      bytes: asset.bytes,
      mime: asset.mime,
      cachedAt: now,
      lastAccessedAt: now,
    };
    await this.deleteEntitySourcesExcept(input);
    await this.cacheStorage.set(getAvatarAssetCacheKey(input), record);
    await this.enforceLimit(input.kind, input.scope);
    return record;
  }

  private async deleteEntitySourcesExcept(input: AvatarAssetCacheInput) {
    const entityPrefix = `${getAvatarAssetCacheEntityPrefix(input)}:`;
    const currentKey = getAvatarAssetCacheKey(input);
    const keys = await this.cacheStorage.keys();
    await Promise.all(
      keys
        .filter(key => key.startsWith(entityPrefix) && key !== currentKey)
        .map(key => this.cacheStorage.del(key))
    );
  }

  private async enforceLimit(
    kind: AvatarAssetCacheKind,
    scope: AvatarAssetCacheScope
  ) {
    const bucketPrefix = `${getAvatarAssetCacheBucketPrefix(kind, scope)}:`;
    const keys = (await this.cacheStorage.keys()).filter(key =>
      key.startsWith(bucketPrefix)
    );
    const records = await Promise.all(
      keys.map(async key => ({
        key,
        record: await this.cacheStorage.get<AvatarAssetCacheRecord>(key),
      }))
    );
    const byEntity = new Map<
      string,
      { key: string; record: AvatarAssetCacheRecord }[]
    >();

    for (const entry of records) {
      if (
        !entry.record ||
        entry.record.version !== CACHE_VERSION ||
        entry.record.kind !== kind ||
        entry.record.scope.serverId !== scope.serverId ||
        entry.record.scope.accountId !== scope.accountId
      ) {
        await this.cacheStorage.del(entry.key);
        continue;
      }
      const entries = byEntity.get(entry.record.entityId) ?? [];
      entries.push(entry as { key: string; record: AvatarAssetCacheRecord });
      byEntity.set(entry.record.entityId, entries);
    }

    const entityWinners = Array.from(byEntity.entries()).map(
      ([entityId, entries]) => {
        entries.sort(
          (a, b) => b.record.lastAccessedAt - a.record.lastAccessedAt
        );
        return { entityId, keep: entries[0], stale: entries.slice(1) };
      }
    );

    for (const entity of entityWinners) {
      await Promise.all(
        entity.stale.map(entry => this.cacheStorage.del(entry.key))
      );
    }

    entityWinners.sort(
      (a, b) => b.keep.record.lastAccessedAt - a.keep.record.lastAccessedAt
    );
    const evicted = entityWinners.slice(avatarAssetCacheLimit(kind));
    await Promise.all(
      evicted.flatMap(entity =>
        [entity.keep, ...entity.stale].map(entry =>
          this.cacheStorage.del(entry.key)
        )
      )
    );
  }
}

export class AvatarAssetCacheService extends Service {
  private readonly cache: AvatarAssetCache;

  constructor(cacheStorage: CacheStorage) {
    super();
    this.cache = new AvatarAssetCache(cacheStorage);
  }

  get(input: AvatarAssetCacheInput) {
    return this.cache.get(input);
  }

  cacheUrl(input: AvatarAssetCacheInput, serverBaseUrl: string) {
    return this.cache.cacheUrl(input, serverBaseUrl);
  }

  objectUrl(input: AvatarAssetCacheInput, serverBaseUrl: string) {
    return this.cache.objectUrl(input, serverBaseUrl);
  }

  cacheBlob(input: AvatarAssetCacheInput, blob: Blob) {
    return this.cache.cacheBlob(input, blob);
  }
}
